import React, { createContext, useContext, useRef, useState } from 'react';
import { useCheckboxGroupState } from 'react-stately';
import { useCheckboxGroup, useCheckboxGroupItem, useId } from 'react-aria';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Group from '../Group';
import AdvancedDescription from '../AdvancedDescription';
import InlineErrors from '../InlineErrors';
import AsLinkButton from '../../buttons/AsLinkButton/AsLinkButton';

const CheckboxContext = createContext(null);

/**
 * A component for selecting multiple options
 */
function CheckboxGroup(props) {
  // Destructure props and assign default values.
  const {
    id,
    size = 'medium',
    label,
    description,
    options,
    advancedDescription,
    advancedDescriptionButtonText,
    errors,
    isRequired,
    isHidden,
    children,
    grouped,
    scrollable,
    groupClasses
  } = props;

  const fieldsetClasses = classNames('group__fieldset', {
    'fieldset--uses-group-elements': grouped,
    'js-fieldset-uses-group-elements': grouped,
    'fieldset--restricted-height': scrollable,
    'js-fieldset-restricted-height': scrollable
  });

  let state = useCheckboxGroupState(props);
  let { radioGroupProps, labelProps, descriptionProps, errorMessageProps } = useCheckboxGroup(props, state);

  return (
    <Group id={id} type="checkboxes" size={size} isRequired={isRequired} isHidden={isHidden} className={groupClasses}>
      <fieldset {...radioGroupProps} className={fieldsetClasses}>
        <legend className="group__legend">
          <span {...labelProps} className="group__label">
            {label}
          </span>
          {description && (
            <span {...(!advancedDescription && descriptionProps)} className="group__description">
              {description}
            </span>
          )}
        </legend>
        <div className="group__elements js-group-elements">
          <CheckboxContext.Provider value={state}>
            {/* The checkbox group items can either be created from options prop or passed in as children. */}
            {options
              ? options.map(({ id: optionId, text, ...optionProps }) => (
                  <CheckboxGroupItem
                    {...optionProps}
                    key={optionId}
                    value={optionId}
                    label={text}
                    data-cw-id={id + '_' + optionId}
                  />
                ))
              : children}
          </CheckboxContext.Provider>
        </div>
        {advancedDescription && (
          <AdvancedDescription
            {...descriptionProps}
            buttonText={advancedDescriptionButtonText}
            text={advancedDescription}
          />
        )}
        {errors && <InlineErrors {...errorMessageProps} messages={errors} />}
      </fieldset>
    </Group>
  );
}

export function CheckboxGroupItem(props) {
  const { label, advancedDescription, advancedDescriptionButtonText } = props;

  // Set children as empty to suppress warning: If you do not provide children, you must specify an aria-label for accessibility.
  // We are setting our own <label /> for accessibility.
  props = { ...props, children: [] };

  let state = useContext(CheckboxContext);
  let ref = useRef(null);
  let { inputProps } = useCheckboxGroupItem(props, state, ref);
  let inputId = useId();
  let labelId = useId();
  let advancedDescriptionId = useId();

  const [isOpen, setIsOpen] = useState(false);

  let isDisabled = state.isDisabled || props.isDisabled;

  const elementClasses = classNames('group__element', 'js-group-element', {
    'element--disabled': isDisabled
  });

  return (
    <div className={elementClasses}>
      <span className="group__input">
        <input
          {...inputProps}
          ref={ref}
          id={inputId}
          aria-labelledby={labelId}
          aria-describedby={advancedDescription ? advancedDescriptionId : inputProps['aria-describedby']}
        />
        <label id={labelId} className="group__label" htmlFor={inputId}>
          <span className="label__checkmark">
            <span className="checkmark__input"></span>
          </span>
          <span>{label}</span>
        </label>
        {advancedDescription && (
          <AsLinkButton
            className={'advanced-description__link js-form-advanced-description-link'}
            onPress={() => setIsOpen(!isOpen)}
          >
            {advancedDescriptionButtonText}
          </AsLinkButton>
        )}
      </span>
      {advancedDescription && (
        <div className="group__advanced-description">
          <div
            className={classNames('advanced-description__content', 'js-form-advanced-description-content', {
              hidden: !isOpen
            })}
          >
            <span id={advancedDescriptionId}>{advancedDescription}</span>
          </div>
        </div>
      )}
    </div>
  );
}

CheckboxGroup.propTypes = {
  /**
   * The id
   */
  id: PropTypes.string,
  /**
   * The field size
   */
  size: PropTypes.string,
  /**
   * The label
   */
  label: PropTypes.string.isRequired,
  /**
   * The description
   */
  description: PropTypes.string,
  /**
   * The options
   */
  options: PropTypes.arrayOf(PropTypes.object),
  /**
   * The advanced description
   */
  advancedDescription: PropTypes.node,
  /**
   * The advanced description button text
   */
  advancedDescriptionButtonText: PropTypes.string,
  /**
   * The errors
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * The required flag
   */
  isRequired: PropTypes.bool,
  /**
   * The hidden flag
   */
  isHidden: PropTypes.bool,
  /**
   * The nested children
   */
  children: PropTypes.node,
  /**
   * The change handler
   */
  onChange: PropTypes.func
};

export default CheckboxGroup;
